// src/components/admin/contract/ContractDashboard.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  faListUl,
  faPlus,
  faFileExcel,
  faChartLine,
  faCheckCircle,
  faHourglassHalf,
  faTimesCircle,
  faFolderOpen,
  faFlagCheckered,
  faFileContract,
  faPenNib,
  faAward,
  faBan,
  faFileInvoice,
  faTruck,
  faTools,
  faPauseCircle,
  faPlayCircle,
  faUpload,
  faThumbsUp,
  faMoneyBillWave,
  faMoneyCheckAlt,
  faHandshake,
  faExclamationTriangle,
  faQuestionCircle,
  faArrowLeft,
  faLock,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  getContractStatusSummary,
  getContractCategorySummary,
} from './ContractApi';
import ContractKpiDashboard from './ContractKpiDashboard';

import { MODULES } from '../../constants/globalConstants';
import {
  readUserFromLocalStorage,
  hasPermission,          // hasPermission(user, moduleName, actions, 'any'|'all')
  getModulePermissionRow, // returns the module permission row for the user+module
} from '../../utils/permissions';
import type { UserDTO } from '../../components/auth/AuthApi';

interface StatusCount {
  status: string;
  count: number;
}

interface CategoryCount {
  statusCategory: string;
  totalCount: number;
}

const statusIcons: Record<string, any> = {
  DRAFT: faPenNib,
  SUBMITTED: faUpload,
  AWARDED: faAward,
  REJECTED: faBan,
  CANCELLED: faTimesCircle,
  WORK_ORDER_ISSUED: faFileInvoice,
  MOBILIZED: faTruck,
  WORK_IN_PROGRESS: faTools,
  ON_HOLD: faPauseCircle,
  RESUMED: faPlayCircle,
  COMPLETED: faCheckCircle,
  SUBMITTED_FOR_APPROVAL: faUpload,
  APPROVED: faThumbsUp,
  PAYMENT_PENDING: faMoneyBillWave,
  PAYMENT_RECEIVED: faMoneyCheckAlt,
  CLOSED: faHandshake,
  TERMINATED: faBan,
  DISPUTE: faExclamationTriangle,
};

const categoryIcons: Record<string, any> = {
  Active: faChartLine,
  Closed: faFlagCheckered,
  'Pre-Project': faPenNib,
  Dispute: faExclamationTriangle,
  Others: faQuestionCircle,
};

const ContractDashboard: React.FC = () => {
  const navigate = useNavigate();

  // role-based access — read the user once
  const user = readUserFromLocalStorage<UserDTO>();

  // contracts permission row (for visibleInDashboard)
  const contractsPermRow = useMemo(
    () => getModulePermissionRow(user || null, MODULES.CONTRACTS) || null,
    [user]
  );

  // show page if user has (view OR report) and the dashboard is visible
  const canSeeDashboard = useMemo(() => {
    const viewOrReport = hasPermission(user || null, MODULES.CONTRACTS, ['view', 'report'], 'any');
    const visibleFlag =
      contractsPermRow && typeof contractsPermRow.visibleInDashboard === 'boolean'
        ? !!contractsPermRow.visibleInDashboard
        : true;
    return viewOrReport && visibleFlag;
  }, [contractsPermRow, user]);

  // header buttons
  const canViewEditOrCreate = useMemo(
    () => hasPermission(user || null, MODULES.CONTRACTS, ['view', 'edit', 'create'], 'any'),
    [user]
  );
  const canCreateContracts = useMemo(
    () => hasPermission(user || null, MODULES.CONTRACTS, 'create', 'any'),
    [user]
  );

  const [statusSummary, setStatusSummary] = useState<StatusCount[]>([]);
  const [categorySummary, setCategorySummary] = useState<CategoryCount[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (!canSeeDashboard) {
      setLoading(false);
      setStatusSummary([]);
      setCategorySummary([]);
      return;
    }

    const load = async () => {
      try {
        setLoading(true);
        const [statusRes, categoryRes] = await Promise.all([
          getContractStatusSummary(),
          getContractCategorySummary(),
        ]);
        setStatusSummary(statusRes.data || []);
        setCategorySummary(categoryRes.data || []);
      } catch (err) {
        console.error('Dashboard loading failed', err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [canSeeDashboard]);

  const handleCardClick = (filters: any) => {
    navigate('/contracts/list', { state: { filters } });
  };

  if (!canSeeDashboard) {
    return (
      <div className="p-6">
        <div className="max-w-2xl mx-auto bg-white border rounded-xl p-6 text-center">
          <div className="flex items-center justify-center mb-3">
            <div className="w-12 h-12 rounded-full border flex items-center justify-center text-slate-600">
              <FontAwesomeIcon icon={faLock} />
            </div>
          </div>
          <h2 className="text-xl font-semibold mb-1">Access Restricted</h2>
          <p className="text-slate-600">
            You don’t have permission to view the Contracts dashboard. If you believe this is an
            error, please contact your administrator.
          </p>
          <div className="mt-4">
            <button
              onClick={() => navigate(-1)}
              className="px-3 py-2 rounded border hover:bg-slate-50"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Top Bar with Back Button and Title */}
      <div className="flex justify-between items-center mb-4">
        <button
          onClick={() => navigate(-1)}
          className="text-gray-600 hover:text-black flex items-center gap-2"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          Back
        </button>
        <h2 className="text-2xl font-semibold text-center flex-grow">Contracts Dashboard</h2>
      </div>

      {/* Action Buttons (role-gated) */}
      <div className="flex flex-wrap gap-3 justify-end">
        {canViewEditOrCreate && (
          <>
            <button
              className="bg-blue-600 text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-blue-700"
              onClick={() => navigate('/contracts/list')}
              title="View all contracts"
            >
              <FontAwesomeIcon icon={faListUl} />
              View All Contracts
            </button>

            <button
              className="bg-purple-600 text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-purple-700"
              onClick={() => navigate('/contracts/contract-deposit')}
              title="Contract deposit"
            >
              <FontAwesomeIcon icon={faMoneyBillWave} />
              Contract Deposit
            </button>
          </>
        )}

        {canCreateContracts && (
          <>
            <button
              className="bg-green-600 text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-green-700"
              onClick={() => navigate('/contracts/create')}
              title="Create contract"
            >
              <FontAwesomeIcon icon={faPlus} />
              Create Contract
            </button>

            <button
              className="bg-gray-700 text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-gray-800"
              onClick={() => navigate('/contracts/bulk-upload')}
              title="Bulk upload contracts"
            >
              <FontAwesomeIcon icon={faFileExcel} />
              Bulk Upload
            </button>
          </>
        )}
      </div>

      {/* Status Summary */}
      <div>
        <h3 className="text-xl font-semibold mb-4">Status Wise Summary</h3>
        {loading ? (
          <div className="text-gray-600">Loading…</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {statusSummary.map((item) => (
              <div
                key={item.status}
                className="bg-white p-4 rounded shadow hover:bg-blue-100 cursor-pointer flex items-center gap-4"
                onClick={() => handleCardClick({ status: [item.status] })}
              >
                <FontAwesomeIcon
                  icon={statusIcons[item.status] || faQuestionCircle}
                  className="text-2xl text-blue-500"
                />
                <div>
                  <div className="text-sm text-gray-700">{item.status}</div>
                  <div className="text-2xl font-bold text-blue-600">{item.count}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Category Summary */}
      <div>
        <h3 className="text-xl font-semibold mb-4 mt-8">Status Category Summary</h3>
        {loading ? (
          <div className="text-gray-600">Loading…</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {categorySummary.map((item) => (
              <div
                key={item.statusCategory}
                className="bg-white p-4 rounded shadow hover:bg-green-100 cursor-pointer flex items-center gap-4"
                onClick={() => handleCardClick({ statusCategory: item.statusCategory })}
              >
                <FontAwesomeIcon
                  icon={categoryIcons[item.statusCategory] || faQuestionCircle}
                  className="text-2xl text-green-600"
                />
                <div>
                  <div className="text-sm text-gray-700">{item.statusCategory}</div>
                  <div className="text-2xl font-bold text-green-600">{item.totalCount}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Contract KPIs (already role-gated inside that component) */}
      <ContractKpiDashboard />
    </div>
  );
};

export default ContractDashboard;